import type { HelpGuide } from '../types';

export const settingsRolesGuide: HelpGuide = {
  key: 'settings-roles',
  title: 'บทบาท & สิทธิ์ (Roles & Permissions)',
  subtitle: 'เมนู: ระบบ ▸ บทบาท & สิทธิ์',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/settings-roles/คู่มือการใช้งาน_บทบาทและสิทธิ์_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “บทบาท & สิทธิ์” ใช้กำหนดว่าผู้ใช้แต่ละกลุ่มทำอะไรในระบบ ERP ได้บ้าง โดยจัดเป็น “บทบาท (Role)” เช่น นักบัญชีสร้างใบสำคัญ หรือ ผู้อนุมัติ แล้วผูก “สิทธิ์ (Permission)” เข้ากับบทบาทนั้น ผู้ดูแลระบบนำบทบาทไปกำหนดให้ผู้ใช้ที่หน้า “ผู้ใช้งาน” อีกที',
        },
        {
          kind: 'callout',
          text: 'หลักการ: ผู้ใช้ → ได้รับ บทบาท → บทบาทถือ สิทธิ์ หลายรายการ การแยกสิทธิ์ตามบทบาทช่วยทำ “การแบ่งแยกหน้าที่ (Separation of Duties)” เช่น คนสร้างใบสำคัญกับคนอนุมัติต้องไม่ใช่คนเดียวกัน',
          variant: 'info',
        },
        {
          kind: 'callout',
          text: 'ต้องมีสิทธิ์ role.manage จึงจะเข้าหน้านี้และแก้ไขบทบาท/สิทธิ์ได้ การเปลี่ยนสิทธิ์มีผลต่อผู้ใช้ทุกคนที่ถือบทบาทนั้น โปรดตรวจสอบก่อนบันทึก',
          variant: 'warn',
        },
        { kind: 'para', text: 'หน้านี้ทำงานหลัก 4 อย่าง:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ดูบทบาททั้งหมด — ', text: 'ตารางรวมทุกบทบาทพร้อมจำนวนสิทธิ์และสถานะระบบ' },
            {
              lead: 'สร้าง/แก้ไข/ลบบทบาท — ',
              text: 'จัดการบทบาทที่สร้างเองได้ (บทบาทระบบลบไม่ได้)',
            },
            {
              lead: 'กำหนดสิทธิ์แบบมุมมองเมนู — ',
              text: 'เลือกระดับการเข้าถึงทีละเมนู (ไม่มีสิทธิ์/อ่าน/เต็ม)',
            },
            {
              lead: 'กำหนดสิทธิ์ขั้นสูง — ',
              text: 'ติ๊กเลือกสิทธิ์รายตัวจัดกลุ่มตามทรัพยากร เพื่อปรับละเอียด',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/settings-roles/01-roles-list.png',
          caption: 'หน้าจัดการบทบาท — ตารางบทบาททั้งหมด พร้อมปุ่มสร้างบทบาทและปุ่มจัดการรายแถว',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'หน้านี้เป็นมุมมองเดียว (ไม่มีแท็บ) ประกอบด้วย:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ปุ่ม + สร้างบทบาท — ', text: 'มุมบนของหน้า ใช้เปิดฟอร์มสร้างบทบาทใหม่' },
            {
              lead: 'ตารางบทบาท — ',
              text: 'คอลัมน์ รหัส · ชื่อ · สิทธิ์ (จำนวน หรือ “ทั้งหมด”) · ระบบ (ใช่/—) และปุ่มจัดการรายแถว',
            },
            {
              lead: 'ปุ่มรายแถว — ',
              text: 'แก้สิทธิ์ (เปิดตัวกำหนดสิทธิ์) · แก้ไข (แก้ชื่อ/คำอธิบาย) · ลบ (เฉพาะบทบาทที่ไม่ใช่ของระบบ)',
            },
          ],
        },
        {
          kind: 'para',
          text: 'คอลัมน์ “สิทธิ์” จะแสดงคำว่า “ทั้งหมด” หากบทบาทนั้นถือสิทธิ์ครอบจักรวาล (*) เช่น ADMIN มิฉะนั้นจะแสดงเป็นจำนวนสิทธิ์ที่ผูกไว้ ส่วนคอลัมน์ “ระบบ” แสดง “ใช่” สำหรับบทบาทระบบที่ลบไม่ได้ และ “—” สำหรับบทบาทที่สร้างเอง',
        },
      ],
    },
    {
      id: 'create',
      heading: '3. การสร้างบทบาทใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม + สร้างบทบาท จะเปิดหน้าต่าง “สร้างบทบาทใหม่” ให้กรอกข้อมูล 3 ช่อง',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'รหัสบทบาท (จำเป็น) — ',
              text: 'รูปแบบ UPPER_SNAKE คือตัวพิมพ์ใหญ่ ตัวเลข และขีดล่างเท่านั้น ยาวไม่เกิน 40 ตัว เช่น ACCOUNTANT_MAKER (กำหนดแล้วแก้ภายหลังไม่ได้)',
            },
            {
              lead: 'ชื่อ (จำเป็น) — ',
              text: 'ชื่อภาษาไทยที่สื่อความหมาย เช่น นักบัญชีสร้างใบสำคัญ',
            },
            { lead: 'คำอธิบาย (ไม่บังคับ) — ', text: 'อธิบายขอบเขตหน้าที่ของบทบาทนี้' },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่ม สร้าง จะใช้งานได้เมื่อกรอก รหัส และ ชื่อ ครบแล้ว เมื่อสร้างเสร็จบทบาทจะยังไม่มีสิทธิ์ใด ๆ ต้องไปกำหนดสิทธิ์ต่อด้วยปุ่ม แก้สิทธิ์',
          tone: 'muted',
        },
        {
          kind: 'image',
          src: '/help/settings-roles/02-create-role.png',
          caption: 'หน้าต่างสร้างบทบาทใหม่ — รหัส (UPPER_SNAKE), ชื่อ และคำอธิบาย',
        },
      ],
    },
    {
      id: 'edit',
      heading: '4. การแก้ไขชื่อและคำอธิบายบทบาท',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม แก้ไข ที่แถวบทบาท จะเปิดหน้าต่าง “แก้ไขบทบาท” โดยมีรหัสบทบาทแสดงเป็นหัวข้อย่อย (แก้รหัสไม่ได้) แก้ไขได้เฉพาะ ชื่อ และ คำอธิบาย จากนั้นกด บันทึก',
        },
        {
          kind: 'callout',
          text: 'รหัสบทบาทเปลี่ยนไม่ได้ เพราะระบบและบันทึกการมอบหมายผู้ใช้อ้างอิงรหัสนี้อยู่ หากต้องการเปลี่ยนรหัสให้สร้างบทบาทใหม่แทน',
          variant: 'info',
        },
        {
          kind: 'image',
          src: '/help/settings-roles/03-edit-role.png',
          caption: 'หน้าต่างแก้ไขบทบาท — แก้ได้เฉพาะชื่อและคำอธิบาย (รหัสคงเดิม)',
        },
      ],
    },
    {
      id: 'permission-tristate',
      heading: '5. การกำหนดสิทธิ์แบบมุมมองเมนู (Tri-state)',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม แก้สิทธิ์ ที่แถวบทบาท จะเปิดหน้าต่าง “สิทธิ์ของบทบาท” มุมมองเริ่มต้นคือ “มุมมองเมนู” ซึ่งแสดงรายการเมนูในระบบ (เช่น บันทึกบัญชี ลูกหนี้การค้า เจ้าหนี้การค้า ฯลฯ) พร้อมปุ่ม 3 ระดับให้เลือกในแต่ละเมนู',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'ไม่มีสิทธิ์ — ', text: 'บทบาทนี้เข้าใช้เมนูดังกล่าวไม่ได้เลย' },
            { lead: 'อ่านอย่างเดียว — ', text: 'ดู/เปิดข้อมูลได้ แต่บันทึกหรือแก้ไขไม่ได้' },
            { lead: 'ทำงานได้เต็ม — ', text: 'ทั้งดูและทำรายการในเมนูนั้นได้ครบ' },
          ],
        },
        {
          kind: 'para',
          text: 'มุมมองนี้เหมาะกับการตั้งสิทธิ์อย่างรวดเร็วทีละเมนู เมื่อเลือกครบแล้วกด บันทึก ระบบจะปรับสิทธิ์ที่มีผลต่อผู้ใช้ทุกคนในบทบาทนั้นทันที',
        },
        {
          kind: 'image',
          src: '/help/settings-roles/04-permission-tristate.png',
          caption:
            'มุมมองเมนู (Tri-state) — เลือกระดับสิทธิ์ ไม่มีสิทธิ์ / อ่านอย่างเดียว / ทำงานได้เต็ม ทีละเมนู',
        },
      ],
    },
    {
      id: 'permission-advanced',
      heading: '6. การกำหนดสิทธิ์ขั้นสูง (รายสิทธิ์)',
      blocks: [
        {
          kind: 'para',
          text: 'ในหน้าต่างสิทธิ์ของบทบาท กดสลับเป็น “ขั้นสูง (รายสิทธิ์)” จะแสดงสิทธิ์รายตัวจัดกลุ่มตามทรัพยากร (เช่น journal, ar, ap, supplier) ให้ติ๊กเลือกเปิด/ปิดเป็นรายสิทธิ์ เช่น journal.create (สร้างใบสำคัญ) หรือ apbill.approve (อนุมัติใบแจ้งหนี้เจ้าหนี้)',
        },
        {
          kind: 'callout',
          text: 'ใช้มุมมองขั้นสูงเมื่อต้องการปรับสิทธิ์ละเอียดเพื่อแบ่งแยกหน้าที่ (SoD) เช่น เปิดเฉพาะสิทธิ์สร้าง แต่ไม่เปิดสิทธิ์อนุมัติในกลุ่มเดียวกัน',
          variant: 'info',
        },
        {
          kind: 'para',
          text: 'หากชุดสิทธิ์ที่เลือกในมุมมองขั้นสูงไม่ตรงกับรูปแบบมาตรฐานของเมนูใด มุมมองเมนูจะแสดงเมนูนั้นเป็น “กำหนดเอง” เลือกครบแล้วกด บันทึก',
          tone: 'muted',
        },
        {
          kind: 'image',
          src: '/help/settings-roles/05-permission-advanced.png',
          caption: 'มุมมองขั้นสูง (รายสิทธิ์) — ติ๊กเลือกสิทธิ์รายตัว จัดกลุ่มตามทรัพยากร',
        },
      ],
    },
    {
      id: 'admin-readonly',
      heading: '7. บทบาท ADMIN และบทบาทระบบ',
      blocks: [
        {
          kind: 'para',
          text: 'บทบาทที่ถือสิทธิ์ครอบจักรวาล (*) เช่น ADMIN จะมีสิทธิ์ทุกอย่างโดยอัตโนมัติ เมื่อกด แก้สิทธิ์ ระบบจะแสดงข้อความว่า “บทบาทนี้มีสิทธิ์ทั้งหมด (*) — แก้ไม่ได้” และปิดการแก้ไขทั้งหมด เพื่อกันการลดสิทธิ์ผู้ดูแลระบบโดยไม่ตั้งใจ',
        },
        {
          kind: 'callout',
          text: 'บทบาทระบบ (ระบบ = “ใช่”) เป็นบทบาทพื้นฐานที่ระบบสร้างให้ ลบไม่ได้ ปุ่ม ลบ จะไม่ปรากฏที่แถวเหล่านี้ ส่วนบทบาทที่สร้างเอง (ระบบ = “—”) จึงจะลบได้',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/settings-roles/06-admin-readonly.png',
          caption:
            'สิทธิ์ของบทบาท ADMIN — มีสิทธิ์ทั้งหมดอัตโนมัติ จึงแสดงแบบอ่านอย่างเดียว แก้ไม่ได้',
        },
      ],
    },
    {
      id: 'delete',
      heading: '8. การลบบทบาท',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม ลบ ที่แถวของบทบาทที่สร้างเอง ระบบจะถามยืนยันก่อนลบ (เช่น “ลบบทบาท {รหัส}?”) เมื่อยืนยันแล้วบทบาทจะถูกลบออกจากรายการ',
        },
        {
          kind: 'callout',
          text: 'ลบได้เฉพาะบทบาทที่ไม่ใช่บทบาทระบบเท่านั้น ก่อนลบควรตรวจสอบว่าไม่มีผู้ใช้คนใดยังถือบทบาทนี้อยู่ เพื่อไม่ให้ผู้ใช้สูญเสียสิทธิ์ที่จำเป็น',
          variant: 'warn',
        },
      ],
    },
    {
      id: 'states',
      heading: '9. สรุปสถานะและระดับสิทธิ์',
      blocks: [
        {
          kind: 'table',
          head: ['รายการ', 'ความหมาย'],
          rows: [
            ['ระบบ = ใช่', 'บทบาทระบบ ลบไม่ได้ (ปุ่มลบไม่ปรากฏ)'],
            ['ระบบ = —', 'บทบาทที่สร้างเอง ลบได้'],
            ['สิทธิ์ = ทั้งหมด', 'ถือสิทธิ์ครอบจักรวาล (*) เช่น ADMIN แก้สิทธิ์ไม่ได้'],
            ['ไม่มีสิทธิ์', 'เข้าเมนูนั้นไม่ได้'],
            ['อ่านอย่างเดียว', 'ดูได้ แต่บันทึก/แก้ไขไม่ได้'],
            ['ทำงานได้เต็ม', 'ดูและทำรายการได้ครบ'],
            ['กำหนดเอง', 'ชุดสิทธิ์ที่เลือกไม่ตรงรูปแบบมาตรฐานของเมนู (ตั้งผ่านมุมมองขั้นสูง)'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '10. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'เปลี่ยนสิทธิ์แล้วมีผลกับใครบ้าง?' },
        {
          kind: 'bullets',
          items: [{ text: 'มีผลกับผู้ใช้ทุกคนที่ถือบทบาทนั้น และมีผลทันทีหลังกดบันทึก' }],
        },
        { kind: 'para', text: 'ทำไมแก้สิทธิ์ของ ADMIN ไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'เพราะ ADMIN ถือสิทธิ์ทั้งหมด (*) อัตโนมัติ ระบบล็อกไว้เพื่อกันการลดสิทธิ์ผู้ดูแลโดยไม่ตั้งใจ',
            },
          ],
        },
        { kind: 'para', text: 'ทำไมบางบทบาทไม่มีปุ่มลบ?' },
        {
          kind: 'bullets',
          items: [{ text: 'เป็นบทบาทระบบ (ระบบ = ใช่) ซึ่งลบไม่ได้ ลบได้เฉพาะบทบาทที่สร้างเอง' }],
        },
        { kind: 'para', text: 'มุมมองเมนูกับมุมมองขั้นสูงต่างกันอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'มุมมองเมนูตั้งสิทธิ์ทีละเมนูแบบ 3 ระดับ (เร็ว) ส่วนมุมมองขั้นสูงเลือกสิทธิ์รายตัวเพื่อปรับละเอียด (เหมาะกับการแบ่งแยกหน้าที่)',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '11. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            { lead: 'บทบาท (Role) — ', text: 'กลุ่มสิทธิ์ที่ตั้งชื่อไว้ นำไปกำหนดให้ผู้ใช้' },
            {
              lead: 'สิทธิ์ (Permission) — ',
              text: 'การอนุญาตให้ทำงานหนึ่งอย่าง เช่น สร้างใบสำคัญ หรือ อนุมัติ',
            },
            {
              lead: 'รหัสบทบาท (UPPER_SNAKE) — ',
              text: 'รหัสตัวพิมพ์ใหญ่+ขีดล่าง เช่น ACCOUNTANT_MAKER แก้ภายหลังไม่ได้',
            },
            { lead: 'บทบาทระบบ — ', text: 'บทบาทพื้นฐานที่ระบบสร้างให้ ลบไม่ได้ (ระบบ = ใช่)' },
            {
              lead: 'สิทธิ์ทั้งหมด (*) — ',
              text: 'สิทธิ์ครอบจักรวาลที่ครอบคลุมทุกอย่าง ถือโดย ADMIN แก้ไม่ได้',
            },
            {
              lead: 'Tri-state — ',
              text: 'การเลือกสิทธิ์ทีละเมนูแบบ 3 ระดับ: ไม่มีสิทธิ์ / อ่านอย่างเดียว / ทำงานได้เต็ม',
            },
            {
              lead: 'การแบ่งแยกหน้าที่ (SoD) — ',
              text: 'การแยกสิทธิ์ผู้สร้างกับผู้อนุมัติออกจากกัน เพื่อควบคุมภายใน',
            },
          ],
        },
      ],
    },
  ],
};
